import type { PreciseLocale } from '@front/i18n/src/types';

import type { UniverseReference } from '../types/commons';

export const UNIVERSES: Record<string, UniverseReference> = {
  MASSAGE: 'massage',
  BEAUTY: 'beauty',
  HAIRCUT: 'haircut',
  CLEANING: 'cleaning',
  CHILDCARE: 'childcare',
  SPORTS_COACHING: 'sports_coaching',
};

/** A root category: a universe reference paired with its locale-specific SEO landing slug. */
export type RootUniverse = { reference: UniverseReference; slug: string };

/**
 * Single source of truth for the root categories, keyed by precise locale.
 * Slugs are the locale-specific SEO landing slugs. Titles are resolved from i18n
 * (`universes.{reference}.title`) via `getRootUniverses` — see utils/universes.ts.
 */
export const ROOT_UNIVERSES = {
  'fr-FR': [
    { reference: UNIVERSES.CLEANING, slug: 'femme-menage-domicile' },
    { reference: UNIVERSES.CHILDCARE, slug: 'garde-enfant' },
    { reference: UNIVERSES.HAIRCUT, slug: 'coiffure-domicile' },
    { reference: UNIVERSES.BEAUTY, slug: 'estheticienne-domicile' },
    { reference: UNIVERSES.MASSAGE, slug: 'massage-domicile' },
  ],
  'en-GB': [
    { reference: UNIVERSES.CLEANING, slug: 'domestic-cleaning' },
    { reference: UNIVERSES.HAIRCUT, slug: 'mobile-hairdresser' },
    { reference: UNIVERSES.BEAUTY, slug: 'mobile-beauty' },
    { reference: UNIVERSES.MASSAGE, slug: 'mobile-massage' },
  ],
  'de-DE': [{ reference: UNIVERSES.CLEANING, slug: 'haushaltshilfe' }],
  'de-AT': [{ reference: UNIVERSES.CLEANING, slug: 'haushaltshilfe' }],
  'de-CH': [{ reference: UNIVERSES.CLEANING, slug: 'haushaltshilfe' }],
  'fr-CH': [],
} as const satisfies Record<PreciseLocale, readonly RootUniverse[]>;

export const WELLNESS_UNIVERSES: UniverseReference[] = ['beauty', 'haircut', 'massage', 'sports_coaching'];

export const UNIVERSE_REFERENCES: UniverseReference[] = Object.values(UNIVERSES);

// TODO: Move this to a categories constants file
export const CATEGORIES = {
  MASSAGE: 'massage',
  MASSAGE_MAN: 'massage_man',
  MASSAGE_WOMAN: 'massage_woman',
  WOMAN: 'woman',
  CHILD: 'child',
  MAN: 'man',
  BODYCARE: 'bodycare',
  HAIR_REMOVAL: 'hair_removal',
  BABYSITTING: 'babysitting',
  RECURRING: 'recurring',
  AT_HOME_COACHING_60_MIN: 'at_home_coaching_60min',
  REMOTE_COACHING_40_MIN: 'remote_coaching_40min',
  REMOTE_COACHING_60_MIN: 'remote_coaching_60min',
};

export const HAIRCUT_COLOR_PRESTATIONS = [
  'woman_color',
  'woman_fullcolor',
  'tie_and_dye',
  'woman_streaks',
  'woman_hair_highlighting',
];

export const HANDS_NAIL_POLISH_PRESTATIONS = [
  'nail_polish',
  'manicure_and_nail_polish',
  'hands_gel_nail_polish',
  'artificial_nails_and_nail_polish',
];

export const FACE_CARE_PRESTATIONS = [
  'radiance_care',
  'purifying_care',
  'face_care_scrub',
  'face_care_anti_ageing',
  'face_care_moisturising',
];

export const SPORTS_COACHING_AT_HOME_CATEGORIES = [CATEGORIES.AT_HOME_COACHING_60_MIN];

export const SPORTS_COACHING_VISIO_CATEGORIES = [CATEGORIES.REMOTE_COACHING_40_MIN, CATEGORIES.REMOTE_COACHING_60_MIN];
